import {useCallback, useEffect, useState} from 'react';
import {BUTTON_STYLE_MAIN, PANEL_STYLE_MAIN} from './styles';
import * as aircoreMedia from '@aircore/aircore-media';
import * as U from './util';
import {Video} from './camera-video';
import {MediaDeviceSelector} from './media-device-selector';
import {VideoConfigSelector} from './video-config-selector';
import {VIDEO_CONFIG_OPTIONS} from './controller';

export function DeviceManagementPanel() {
  const [cameraPermission, setCameraPermission] = useState<string | null>(null);
  const [micPermission, setMicPermission] = useState<string | null>(null);
  const [videoDeviceList, setVideoDeviceList] = useState<MediaDeviceInfo[]>([]);
  const [videoDevice, setVideoDevice] = useState(U.getDefaultMediaDevice());
  const [videoConfig, setVideoConfig] = useState<string>();
  const [manualConstraints, setManualConstraints] = useState<MediaTrackConstraints>();
  const [isPreviewingVideo, setIsPreviewingVideo] = useState(false);
  const [camera, setCamera] = useState<aircoreMedia.Camera | null>(null);
  const [cameraPreviewInstance, setCameraPreviewInstance] = useState<aircoreMedia.CameraPreview | null>(null);

  const handleQueryPermission = useCallback(async () => {
    let camPermission;
    let micPermission;
    try {
      camPermission = await aircoreMedia.Engine.sharedInstance.deviceManager.queryCameraPermission();
    } catch (e) {
      camPermission = 'unknown';
      U.appLogO('Query Camera permissions not supported by browser, unknown', e);
    }
    try {
      micPermission = await aircoreMedia.Engine.sharedInstance.deviceManager.queryMicrophonePermission();
    } catch (e) {
      micPermission = 'unknown';
      U.appLogO('Query Microphone permissions not supported by browser, unknown', e);
    }
    setCameraPermission(camPermission);
    setMicPermission(micPermission);
  }, []);

  const handleSelectPreviewVideoDevice = useCallback((device: MediaDeviceInfo) => {
    U.appLog(`handleSelectPreviewVideoDevice ${device.label}`);
    setVideoDevice(device);
  }, []);

  const fetchVideoDevices = useCallback(async () => {
    let devices: MediaDeviceInfo[] = [];

    try {
      devices = await aircoreMedia.Engine.sharedInstance.deviceManager.getVideoDevices();
    } catch (error) {
      U.appLog(`Failed to fetch video devices: ${String(error)}`);
    }

    U.appLogO(
      `Queried for device list to populate video source menu. Found ${devices.length} video input devices:`,
      devices.map(d => {
        return {label: d.label, deviceId: d.deviceId};
      })
    );

    // Browsers don't provide defaults for video devices like they do for audio devices so we make our own
    setVideoDeviceList(devices);

    const deviceIds = devices.map(device => device.deviceId);
    if (!deviceIds.includes(videoDevice.deviceId)) {
      U.appLog('Stale video device detected, reverting to default');
      handleSelectPreviewVideoDevice(devices[0] ?? U.getDefaultMediaDevice());
      if (isPreviewingVideo) {
        U.appLog('Swapping video input for live preview');
        void handleChangePreviewVideo();
      }
    }
  }, [videoDevice, isPreviewingVideo]);

  // Run when the component mounts to initialize the device lists
  useEffect(() => {
    void fetchVideoDevices();
  }, []);

  const startPreview = useCallback(async () => {
    let camera;
    const device = videoDevice.deviceId === 'default' ? undefined : videoDevice.deviceId;
    try {
      if (manualConstraints) {
        U.appLog('Getting camera with custom constraints');
        camera = await aircoreMedia.Engine.sharedInstance.deviceManager.getCameraWithConstraints(
          true,
          manualConstraints
        );
      } else if (videoConfig) {
        U.appLog(`Getting camera with config: ${videoConfig} ...`);
        const config = VIDEO_CONFIG_OPTIONS[videoConfig as keyof typeof VIDEO_CONFIG_OPTIONS];
        camera = await aircoreMedia.Engine.sharedInstance.deviceManager.getCamera(
          true,
          device,
          config.width,
          config.height,
          config.frameRate
        );
      } else {
        U.appLog('Getting camera with default config ...');
        camera = await aircoreMedia.Engine.sharedInstance.deviceManager.getCamera(true, device);
      }
    } catch (err) {
      U.appLog(`Error getting camera: ${String(err)}`);
      camera = null;
    }

    if (camera) {
      U.appLogO('Previewing camera with settings:', camera.settings);
      setCamera(camera);
      setCameraPreviewInstance(new aircoreMedia.CameraPreview(camera));
      setIsPreviewingVideo(true);
    }
  }, [videoDevice, videoConfig, manualConstraints]);

  const stopPreview = useCallback(() => {
    if (camera) {
      camera.stop();
    }
    setCamera(null);
    setCameraPreviewInstance(null);
    setIsPreviewingVideo(false);
  }, [camera]);

  const handlePreviewVideoToggle = useCallback(async () => {
    if (isPreviewingVideo) {
      stopPreview();
    } else {
      await startPreview();
    }
  }, [isPreviewingVideo, camera, videoDevice, videoConfig, manualConstraints]);

  const handleChangePreviewVideo = useCallback(async () => {
    U.appLog('Changing video input for live preview');
    stopPreview();
    await startPreview();
  }, [isPreviewingVideo, camera, videoDevice, videoConfig, manualConstraints]);

  return (
    <div className={PANEL_STYLE_MAIN}>
      <div className="flex flex-col w-full h-auto border-b-2 border-gray-500 text-sm bg-gray-200 pl-1">
        <div className="w-full h-auto">
          <span className="mr-1 font-bold">Device Permissions</span>
        </div>

        <div className="w-full h-auto flex flex-row flex-wrap gap-2 my-1">
          <button className={BUTTON_STYLE_MAIN} onClick={() => void handleQueryPermission()}>
            Query Permissions
          </button>
          <span>
            {'  '}Camera Permission: {cameraPermission ?? 'unknown'}, Microphone Permission:{' '}
            {micPermission ?? 'unknown'}
          </span>
        </div>
      </div>
      <div className="flex flex-col w-full h-auto border-b-2 border-gray-500 text-sm bg-gray-200 pl-1">
        <div className="w-full h-auto">
          <span className="mr-1 font-bold">Camera Preview</span>
        </div>

        <div className="w-full h-auto flex flex-row flex-wrap gap-2 my-1">
          <button className={BUTTON_STYLE_MAIN} onClick={() => void handlePreviewVideoToggle()}>
            {isPreviewingVideo ? 'Stop Preview' : 'Preview Camera'}
          </button>
          <MediaDeviceSelector
            title="Video Source"
            deviceList={videoDeviceList}
            onSelectDevice={handleSelectPreviewVideoDevice}
            loadDeviceList={fetchVideoDevices}
          />
          <VideoConfigSelector
            handleSetConfig={setVideoConfig}
            handleSetManualConstraints={setManualConstraints}
            initialManualConstraints={manualConstraints}
          />
          <button
            className={BUTTON_STYLE_MAIN}
            onClick={() => void handleChangePreviewVideo()}
            hidden={!isPreviewingVideo}
          >
            Change Video
          </button>
        </div>
      </div>
      {isPreviewingVideo && cameraPreviewInstance ? (
        <Video source={cameraPreviewInstance} isMuted={false} />
      ) : (
        <div className="w-full h-auto text-sm text-gray-500">No camera preview</div>
      )}
    </div>
  );
}
